import React, { useCallback, useEffect, useState } from 'react';
import { AlertCircle, LoaderCircle } from 'lucide-react';

import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { PANORAMA_TABLET_DIALOG_CLOSE_CLASS } from '../constants/constants';
import { usePhotoData } from '../hooks';
import Ironview360PanoramaExperience from './Ironview360PanoramaExperience';

export interface HotspotPanoramaTarget {
  id: number;
  photo_id: number;
  pitch: number;
  yaw: number;
}

interface HotspotPanoramaDialogProps {
  open: boolean;
  hotspot: HotspotPanoramaTarget;
  projectId: string;
  onOpenChange: (open: boolean) => void;
}

/**
 * Opens a linked hotspot directly over the current page without changing the route.
 */
const HotspotPanoramaDialog: React.FC<HotspotPanoramaDialogProps> = ({
  open,
  hotspot,
  projectId,
  onOpenChange,
}) => {
  const [activePhotoId, setActivePhotoId] = useState(hotspot.photo_id);
  const [targetCoordinates, setTargetCoordinates] = useState<{
    pitch: number;
    yaw: number;
  } | null>(null);
  const { photoData, hotspotsData, isLoading, error, refetchPhoto, refetchHotspots } =
    usePhotoData(open ? activePhotoId : undefined, projectId);

  useEffect(() => {
    if (!open) return;

    setActivePhotoId(hotspot.photo_id);
    // Match the main viewer's framing so the marker sits just below center.
    setTargetCoordinates({ pitch: hotspot.pitch - 5, yaw: hotspot.yaw });
  }, [hotspot.id, hotspot.photo_id, hotspot.pitch, hotspot.yaw, open]);

  const handleNavigateToPhotoId = useCallback((photoId: number) => {
    setTargetCoordinates(null);
    setActivePhotoId(photoId);
  }, []);

  const handleRetry = useCallback(() => {
    void Promise.all([refetchPhoto(), refetchHotspots()]);
  }, [refetchHotspots, refetchPhoto]);

  return (
    <Dialog modal={false} open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="h-[calc(var(--app-vh,1vh)*100)] max-h-[calc(var(--app-vh,1vh)*100)] w-screen max-w-none rounded-none border-0 p-0 md:h-[calc(var(--app-vh,1vh)*90)] md:max-h-[calc(var(--app-vh,1vh)*90)] md:w-[90vw] md:max-w-[90vw] md:rounded-xl"
        closeButtonClassName={PANORAMA_TABLET_DIALOG_CLOSE_CLASS}
        data-testid="punch-hotspot-panorama-dialog"
      >
        <DialogHeader className="sr-only">
          <DialogTitle>Linked punch item hotspot</DialogTitle>
          <DialogDescription>
            Panorama focused on the hotspot linked to this punch item.
          </DialogDescription>
        </DialogHeader>

        {photoData?.image_url ? (
          <Ironview360PanoramaExperience
            photoId={activePhotoId}
            imageUrl={photoData.image_url}
            photoData={photoData}
            hotspotsData={hotspotsData}
            projectId={projectId}
            onNavigateToPhotoId={handleNavigateToPhotoId}
            onHotspotCreated={async () => {
              await refetchHotspots();
            }}
            onPanoramaError={async () => {
              await refetchPhoto();
            }}
            targetCoordinates={targetCoordinates}
            onTargetReached={() => setTargetCoordinates(null)}
          />
        ) : error ? (
          <div className="flex h-full flex-col items-center justify-center gap-4 bg-background p-6 text-center">
            <AlertCircle className="h-8 w-8 text-destructive" />
            <div>
              <p className="font-display font-bold">Unable to load panorama</p>
              <p className="mt-1 text-sm text-muted-foreground">
                Check your connection and try again.
              </p>
            </div>
            <Button type="button" variant="outline" onClick={handleRetry}>
              Try again
            </Button>
          </div>
        ) : (
          <div className="flex h-full items-center justify-center gap-2 bg-black text-white">
            <LoaderCircle className="h-5 w-5 animate-spin" />
            <span>{isLoading ? 'Loading panorama…' : 'Preparing panorama…'}</span>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default HotspotPanoramaDialog;
